import { Link } from "@tanstack/react-router";
import type { Ref } from "react";
import { cx } from "../../ui";
import { dueState, formatDue, initialOf, SOURCE_LABEL, type DueState, type Task } from "./model";

/** The row's due date has no border: the colour alone carries urgency, the words carry it to screen readers. */
const DUE_TEXT: Record<DueState, string> = {
  overdue: "text-danger",
  today: "text-accent font-semibold",
  soon: "text-accent",
  later: "text-muted",
  none: "",
};

export const DUE_SR: Record<DueState, string> = { overdue: "Overdue: ", today: "Due ", soon: "Due soon: ", later: "Due ", none: "" };

export function Badge({ children, className }: { children: string; className?: string }) {
  return (
    <span className={cx("inline-flex items-center rounded border border-border px-1.5 text-[11px] leading-5 text-muted", className)}>
      {children}
    </span>
  );
}

/** A round checkbox: a native one, transparent and laid over a drawn circle that fills with a tick. */
export function RoundCheck({
  checked,
  disabled,
  label,
  onChange,
  size = "md",
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: () => void;
  size?: "md" | "lg";
}) {
  return (
    <label className={cx("relative -m-1.5 flex shrink-0 p-1.5", disabled === true ? "cursor-default" : "cursor-pointer")}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} aria-label={label} className="peer absolute inset-0 m-0 size-full cursor-pointer appearance-none opacity-0 disabled:cursor-default" />
      <span
        aria-hidden="true"
        className={cx(
          "grid place-items-center rounded-full border-[1.5px] border-muted text-transparent transition-colors",
          "peer-checked:border-accent peer-checked:bg-accent peer-checked:text-accent-contrast",
          "peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-accent peer-disabled:opacity-50",
          size === "lg" ? "mt-1.5 size-[22px]" : "mt-0.5 size-[18px]",
        )}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="size-3">
          <path d="M6 12l4 4 8-8" />
        </svg>
      </span>
    </label>
  );
}

/** A muted line under the title: where the task came from, and that it is private. */
function detailLine(task: Task, showVisibility: boolean): string | null {
  const parts = [
    task.source === null ? null : `From ${SOURCE_LABEL[task.source.kind]} · ${task.createdAt.toLocaleDateString(undefined, { day: "numeric", month: "short" })}`,
    showVisibility && task.visibility === "private" ? "Private" : null,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? null : parts.join(" · ");
}

/** One task: a round done checkbox, the title (opens the task's page), its due date and assignee. */
export function TaskRow({
  workspaceId,
  task,
  now,
  active,
  assigneeLabel,
  showVisibility,
  rowRef,
  onToggle,
  onFocus,
}: {
  workspaceId: string;
  task: Task;
  now: Date;
  active: boolean;
  assigneeLabel: string | null;
  /** False in Personal, where every task is private and saying so would be noise. */
  showVisibility: boolean;
  rowRef?: Ref<HTMLLIElement>;
  onToggle: () => void;
  onFocus: () => void;
}) {
  const closed = task.status !== "open";
  const state = closed ? "none" : dueState(task.due, now);
  const detail = detailLine(task, showVisibility);
  return (
    <li
      ref={rowRef}
      tabIndex={-1}
      onFocus={onFocus}
      aria-current={active ? "true" : undefined}
      data-task-id={task.id}
      className={cx("group flex items-start gap-3 rounded-lg px-2 py-2 focus:outline-none", active ? "bg-surface-2" : "hover:bg-surface-2")}
    >
      <RoundCheck
        checked={task.status === "done"}
        disabled={task.status === "cancelled"}
        label={task.status === "done" ? `Reopen “${task.title}”` : `Mark “${task.title}” done`}
        onChange={onToggle}
      />
      <div className="min-w-0 flex-1">
        <Link
          to="/w/$workspaceId/tasks/$taskId"
          params={{ workspaceId, taskId: task.id }}
          className={cx("block max-w-full break-words text-sm text-text hover:underline", closed ? "text-muted line-through" : "")}
        >
          {task.title}
        </Link>
        {detail === null ? null : <p className="mt-0.5 truncate text-xs text-muted">{detail}</p>}
      </div>
      {task.due === null || closed ? null : (
        <span className={cx("shrink-0 pt-0.5 text-xs", DUE_TEXT[state])}>
          <span className="sr-only">{DUE_SR[state]}</span>
          {formatDue(task.due, now)}
        </span>
      )}
      {assigneeLabel === null ? null : (
        <span
          title={`Assigned to ${assigneeLabel}`}
          className="grid size-6 shrink-0 place-items-center rounded-full bg-surface-2 text-[11px] font-semibold text-muted"
        >
          <span aria-hidden="true">{initialOf(assigneeLabel)}</span>
          <span className="sr-only">{`Assigned to ${assigneeLabel}`}</span>
        </span>
      )}
    </li>
  );
}
